import { useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import {
  FetchPaymentMethodsOfTransactionAccountForDetailPageQuery,
  FetchPaymentMethodsOfTransactionAccountForDetailPageQueryVariables,
  FetchTransactionAccountForAccountDetailsScreenQuery,
  FetchTransactionAccountForAccountDetailsScreenQueryVariables,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { PPC_PAGE_TITLE, ENABLE_DESIGN_ITERATION_1, routePaths } from '@css-xp/ppc-constants';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants/src/ppc-feature-gates';
import {
  AddPaymentMethod,
  AppContent,
  AppPage,
  EditPaymentMethod,
  ErrorComponents,
  PageTabs,
  useFeatureToggle,
  withApollo,
} from '@css-xp/base';
//@ts-ignore
import PPCHeader from '@css-xp/base/assets/img/ppc-header.png';
import { useApplicationContext } from '@css-xp/base/common-context';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { EmailTable } from '@css-xp/ppc-email-table';
import { OrderHistoryTable } from '@css-xp/ppc-order-history-table';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { CARD_PAYMENTS_SIDEBAR } from '../common/types';
import { DETAILS } from '../constants/details';
import { fetchPaymentMethodsForTransactionAccount } from '../queries/fetch-payment-methods';
import { fetchTransactionAccountForAccountDetailsScreen } from '../queries/fetch-transaction-account-for-account-details';
import { AuditTrailTab, BillingAdminsTab, DiscountTab } from './components';
import { PaymentMethods } from './components/payment-methods';
import { AddBillingAdmin, RemovePaymentMethod, AssociatedBillingProfilesTable } from './components/sidebar-panel';
import { DetailPageSummary } from './detail-page-summary';
import { useGetBillingAdminIdsFromAns, useGetRelatedDomainsForCurrentTxA, useTitle } from '@css-xp/ppc-custom-hooks';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import {
  emptyStatePropForOrderHistoryTabOnDetailPage,
  emptyStatePropForWrongSearch,
} from '@css-xp/ppc-common/src/state-props';
import './detail-page.scss';

export interface Address {
  postalCode?: Maybe<string>;
  street?: Maybe<string>;
  city?: Maybe<string>;
  state?: Maybe<string>;
  country?: Maybe<string>;
}

export enum SIDEBAR {
  ADD_BILLING_ADMINS = 'addBillingAdmins',
  REMOVE_PAYMENT_METHOD = 'removePaymentMethod',
  VIEW_BILLING_PROFILE = 'viewBillingProfile',
}

export const SoldToAddressWrapper = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <div>
      <h4 className="mb-20">{label}</h4>
      {children}
    </div>
  );
};

export const DetailPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const context = useApplicationContext();
  const selectedPartnerAccountData = context.partnerAccounts.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );
  useTitle(selectedPartnerAccountData?.name ?? '', PPC_PAGE_TITLE.BILLING_ACCOUNT);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  /**
   * using both the flags from LD and statsig for the time being until we thoroughly test statsig flags value in all the environments.
   * This will be removed later.
   */
  const isPaypalSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isEmailsImprovementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EMAILS_IMPROVEMENT);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const shouldUseIULDetailsViaAuth = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_BALANCE_DATA_USAGE_VIA_AUTH);
  const isACHPaymentMethodEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const {
    billingAdminIds,
    areBillingAdminIdsBeingFetched,
    isLoggedInUserCustomerAdvocate,
  } = useGetBillingAdminIdsFromAns();

  // To improve: Check if FetchPaymentMethodsForTransactionAccountQuery can be avoided
  const {
    data: paymentMethodsData,
    loading: paymentDataLoading,
    error: paymentDataError,
    refetch: paymentDataRefetch,
  } = useQuery<
    FetchPaymentMethodsOfTransactionAccountForDetailPageQuery,
    FetchPaymentMethodsOfTransactionAccountForDetailPageQueryVariables
  >(fetchPaymentMethodsForTransactionAccount, {
    variables: {
      id: txaId || '',
      fetchAch: isACHPaymentMethodEnabled,
    },
    skip: !txaId,
  });

  const { data: accountDetailsData, loading: accountDetailsLoading, error: accountDetailsError } = useQuery<
    FetchTransactionAccountForAccountDetailsScreenQuery,
    FetchTransactionAccountForAccountDetailsScreenQueryVariables
  >(fetchTransactionAccountForAccountDetailsScreen, {
    variables: {
      id: txaId || '',
    },
    skip: !txaId,
  });

  const { relatedDomains, isLoading: relatedDomainsLoading } = useGetRelatedDomainsForCurrentTxA();

  const allowedSidebars = {
    [SIDEBAR.ADD_BILLING_ADMINS]: AddBillingAdmin,
    [CARD_PAYMENTS_SIDEBAR.ADD_PAYMENT_METHOD]: AddPaymentMethod,
    [CARD_PAYMENTS_SIDEBAR.EDIT_PAYMENT_METHOD]: EditPaymentMethod,
    [SIDEBAR.REMOVE_PAYMENT_METHOD]: RemovePaymentMethod,
    [SIDEBAR.VIEW_BILLING_PROFILE]: isDesignIteration1Enabled
      ? withApollo(AssociatedBillingProfilesTable)
      : AssociatedBillingProfilesTable,
  };

  const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
  if (paymentDataError || accountDetailsError) {
    return (
      <div data-testid="ppc-payment-methods-error-container">
        <GenericError error={paymentDataError} />
      </div>
    );
  }

  const getHeaderBackground = () => {
    return <img src={PPCHeader} />;
  };

  const renderPageHeader = () => {
    const title = selectedPartnerAccountData?.name ?? 'Partner account';

    if (isDesignIteration1Enabled) {
      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
        {
          title: 'Billing account',
          href: routePaths.details,
        },
      ];

      return <PageBackgroundV2 heading={title} breadcrumbsProps={{ items: breadcrumbsItems }} />;
    } else {
      return (
        <div className="detail-page-header" data-testId="detail-page-header">
          {getHeaderBackground()}
          <div className="billing-account-name">{title}</div>
        </div>
      );
    }
  };

  const renderDetailPageSummary = (label = '') => {
    return (
      <DetailPageSummary
        paymentMethodsData={paymentMethodsData}
        partnerAccountData={selectedPartnerAccountData}
        paymentDataLoading={paymentDataLoading}
        accountDetailsData={accountDetailsData}
        accountDetailsLoading={accountDetailsLoading}
        partnerDomains={relatedDomains}
        isPartnerAccountDomainsLoading={relatedDomainsLoading}
        label={label}
        {...(isDesignIteration1Enabled && { layoutAppearance: LayoutAppearance.ONE_COLUMN })}
        isIULPhase2Enabled={isIULPhase2Enabled}
        shouldUseIULDetailsViaAuth={shouldUseIULDetailsViaAuth}
      />
    );
  };

  return (
    <AppPage
      allowedSidebars={allowedSidebars}
      className={isDesignIteration1Enabled ? 'detail-page-v2' : 'app-page-v2 detail-page'}
    >
      {renderPageHeader()}
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_DETAILS_SCREEN,
          attributes: {},
        }}
      />
      <AppContent className="detail-page-content">
        {selectedPartnerAccountData && !isDesignIteration1Enabled && renderDetailPageSummary()}
        <PageTabs className="detail-page-tab-container" showAllTab={false}>
          <BillingAdminsTab
            label={isDesignIteration1Enabled ? DETAILS.ADMINS : DETAILS.BILLING_ADMINS}
            transactionAccountId={txaId}
            partnerAccountId={selectedPartnerAccountData?.id}
            validDomains={relatedDomains}
            // These are required for advocates to access PPC. https://hello.jira.atlassian.cloud/browse/ITFO-15975
            isLoggedInUserACustomerAdvocate={isLoggedInUserCustomerAdvocate}
            areBillingAdminIdsBeingFetched={areBillingAdminIdsBeingFetched}
            billingAdminIds={billingAdminIds as string[]}
            version={isDesignIteration1Enabled ? 2 : undefined}
            isLoading={relatedDomainsLoading}
          />
          <PaymentMethods
            label={DETAILS.PAYMENT_METHODS}
            data={paymentMethodsData}
            loading={paymentDataLoading}
            refetch={paymentDataRefetch}
            sidebarClassName="payment-sidebar"
            isDesignIteration1Enabled={isDesignIteration1Enabled}
            {...(isDesignIteration1Enabled && { layoutAppearance: LayoutAppearance.ONE_COLUMN })}
            isPaypalSupportEnabled={isPaypalSupportEnabled}
            isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
            isACHPaymentMethodEnabled={isACHPaymentMethodEnabled}
            selectedPartnerCountry={selectedPartnerAccountData?.billTo?.country}
            creditCardHeading={isRedesignFastFollowEnabled && 'Accepted cards'}
          />
          {selectedPartnerAccountData && (
            <DiscountTab
              label={DETAILS.DISCOUNT}
              data={selectedPartnerAccountData}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          )}
          <AuditTrailTab label={DETAILS.AUDIT_TRAIL} id={txaId} isDesignIteration1Enabled={isDesignIteration1Enabled} />
          <EmailTable
            label={DETAILS.EMAILS}
            transactionAccountId={txaId}
            partnerAccountId={selectedPartnerAccountData?.id}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
            isEmailsImprovementEnabled={isEmailsImprovementEnabled}
          />
          <OrderHistoryTable
            label={DETAILS.ORDER_HISTORY}
            transactionAccountId={txaId}
            {...(isDesignIteration1Enabled
              ? {
                  emptyStateProp: emptyStatePropForOrderHistoryTabOnDetailPage,
                  emptyStatePropForSearch: emptyStatePropForWrongSearch,
                }
              : {})}
          />
          {isDesignIteration1Enabled && renderDetailPageSummary(DETAILS.DETAILS)}
        </PageTabs>
      </AppContent>
    </AppPage>
  );
};
